import type { BookingState } from "../page";

interface BookingSuccessProps {
  booking: BookingState;
  total: number;
}

function generateRef() {
  return "CL" + Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").substring(0, 6);
}

const ref = generateRef();

export default function BookingSuccess({ booking, total }: BookingSuccessProps) {
  const formattedDate = booking.date
    ? new Date(booking.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-cl-tint flex flex-col">
      <nav className="bg-white border-b-4 border-cl-dark sticky top-0 z-50">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1 cursor-pointer">
            <span className="text-2xl font-black tracking-tight text-cl-dark">
              <span className="text-cl-green">C</span>leario
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-cl-green mb-4 inline-block border-2 border-cl-dark"></span>
          </Link>
          <a href="tel:03335775445" className="hidden sm:flex items-center gap-2 text-sm font-bold text-cl-dark">
            <i className="ri-phone-fill text-cl-green"></i>
            03335 775445
          </a>
        </div>
      </nav>

      <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-cl-green rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-[#4a8c0a] cartoon-shadow-green">
              <i className="ri-check-line text-white text-5xl font-black"></i>
            </div>
            <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-4 thick-border cartoon-shadow-sm">
              <span className="w-2 h-2 bg-cl-green rounded-full animate-pulse"></span>
              <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Booking Confirmed</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-cl-dark mb-3">
              You&apos;re all booked in!
            </h1>
            <p className="text-cl-dark/60 font-semibold text-lg">
              We&apos;ll call to confirm your time window. Get ready to wave goodbye to the rubbish.
            </p>
          </div>

          <div className="bg-cl-dark rounded-2xl px-7 py-5 mb-6 cartoon-card text-center">
            <p className="text-white/60 text-sm font-semibold mb-1">Your booking reference</p>
            <p className="text-cl-lime font-black text-3xl tracking-widest mb-2">{ref}</p>
            <p className="text-white/50 text-xs">Keep this safe — you&apos;ll need it if you need to make any changes.</p>
          </div>

          <div className="bg-white rounded-2xl cartoon-card p-7 mb-6">
            <h3 className="font-black text-cl-dark text-base mb-5 flex items-center gap-2">
              <i className="ri-file-list-3-line text-cl-green"></i>
              Booking Summary
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green flex-shrink-0">
                  <i className="ri-customer-service-2-line text-cl-green text-sm"></i>
                </span>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-wide">Service</p>
                  <p className="text-sm font-black text-cl-dark">{booking.serviceTitle}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green flex-shrink-0">
                  <i className="ri-map-pin-line text-cl-green text-sm"></i>
                </span>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-wide">Location</p>
                  <p className="text-sm font-black text-cl-dark">{booking.postcode}</p>
                  {booking.addressLine1 && <p className="text-xs text-gray-500">{booking.addressLine1}{booking.city ? `, ${booking.city}` : ""}</p>}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green flex-shrink-0">
                  <i className="ri-calendar-check-line text-cl-green text-sm"></i>
                </span>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-wide">Collection Date</p>
                  <p className="text-sm font-black text-cl-dark">{formattedDate}</p>
                  {booking.timeSlotLabel && <p className="text-xs text-gray-500">{booking.timeSlotLabel}</p>}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green flex-shrink-0">
                  <i className="ri-money-pound-circle-line text-cl-green text-sm"></i>
                </span>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-wide">Total (inc. VAT)</p>
                  <p className="text-lg font-black text-cl-green">£{total.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl cartoon-card p-7 mb-8">
            <h3 className="font-black text-cl-dark text-base mb-5 flex items-center gap-2">
              <i className="ri-route-line text-cl-green"></i>
              What happens next
            </h3>
            <div className="flex flex-col gap-4">
              {[
                { icon: "ri-phone-line", title: "We'll call to confirm", desc: "Our team will call within 2 hours to confirm your exact arrival window on the day." },
                { icon: "ri-mail-line", title: "Confirmation email", desc: `A booking confirmation will be sent to ${booking.email || "your email"} shortly.` },
                { icon: "ri-map-2-line", title: "Live tracking on the day", desc: "You'll get a text with a live tracker link so you can follow your team from depot to door." },
                { icon: "ri-file-text-line", title: "Waste transfer note", desc: "After collection you'll receive your legal waste transfer note by email within 24 hours." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green flex-shrink-0 text-cl-green">
                    <i className={`${item.icon} text-sm`}></i>
                  </span>
                  <div>
                    <p className="text-sm font-black text-cl-dark">{item.title}</p>
                    <p className="text-xs text-gray-500 font-semibold leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/" className="flex-1 bg-cl-dark text-white font-black py-4 rounded-xl text-center text-sm cartoon-btn whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer">
              <i className="ri-home-line"></i>
              Back to Homepage
            </Link>
            <a href="tel:03335775445" className="flex-1 bg-white text-cl-dark font-black py-4 rounded-xl text-center text-sm cartoon-card whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer hover:bg-cl-lime transition-colors">
              <i className="ri-phone-line text-cl-green"></i>
              Call Us: 03335 775445
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
